import { Box, Textarea } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { MdArrowUpward, MdStop } from 'react-icons/md';
import { useChatSocket } from '../../services/chatSocket';
import { MCustomIconButton, MFlex } from '../Monetize';
import { ChatSessionId } from './ChatSessionId';
import { useMessageHistory } from './useMessageHistory';

export interface MessageInputProps {
  onSendMessage: (message: string) => void;
  /** Messages the user has already sent, oldest first, for ArrowUp recall. */
  previousMessages: string[];
  isLoading?: boolean;
}

const MAX_HEIGHT = 160;

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  previousMessages,
  isLoading,
}) => {
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { disconnect } = useChatSocket();
  const { handleHistoryKeyDown, resetHistoryNavigation } = useMessageHistory({
    history: previousMessages,
    message,
    setMessage,
  });

  // The panel only mounts this when it opens (or finishes restoring), so
  // landing in the composer means keyboard users can type straight away
  // instead of tabbing through the page behind it.
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !isLoading) {
      onSendMessage(message.trim());
      setMessage('');
      resetHistoryNavigation();
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (handleHistoryKeyDown(e)) {
      autoResize();
      return;
    }
    // Enter also confirms an IME candidate (CJK input); that must not send.
    // Safari ends the composition before this keydown, so there isComposing
    // is already false and only keyCode 229 gives it away.
    const isImeConfirmation = e.nativeEvent.isComposing || e.keyCode === 229;
    if (e.key === 'Enter' && !e.shiftKey && !isImeConfirmation) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    if (isLoading) {
      e.preventDefault();
      disconnect();
    }
  };

  const canSend = message.trim().length > 0 && !isLoading;

  return (
    <Box px={4} pt={2} pb={3} bg="bg.canvas">
      <Box
        position="relative"
        bg="bg.surface"
        borderRadius="2xl"
        border="1px solid"
        borderColor={isFocused ? 'tMagenta.base' : 'border.default'}
        boxShadow={
          isFocused
            ? '0 0 0 3px rgba(140, 50, 255, 0.15), 0 4px 12px rgba(7, 7, 35, 0.06)'
            : '0 1px 3px rgba(7, 7, 35, 0.06)'
        }
        transition="all 0.15s ease"
        _hover={{
          borderColor: isFocused ? 'tMagenta.base' : 'tMagenta.200',
        }}
        display="flex"
        alignItems="flex-end"
        gap={2}
        py={2}
        pl={4}
        pr={2}
        asChild
      >
        <form onSubmit={handleSubmit}>
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              autoResize();
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            aria-label="Message Monty"
            placeholder="Ask Monty anything..."
            rows={1}
            unstyled
            resize="none"
            minH="24px"
            maxH={`${MAX_HEIGHT}px`}
            py={1.5}
            px={0}
            fontSize="sm"
            lineHeight="1.5"
            color="fg.primary"
            flex={1}
            overflowY="auto"
            border="none"
            borderRadius={0}
            bg="transparent"
            boxShadow="none !important"
            _focus={{ boxShadow: 'none !important', border: 'none' }}
            _focusVisible={{ boxShadow: 'none !important', border: 'none' }}
            _hover={{ border: 'none' }}
            css={{
              '&::placeholder': { color: 'fg.subtle' },
            }}
          />
          <MCustomIconButton
            type={isLoading ? 'button' : 'submit'}
            aria-label={isLoading ? 'Stop generation' : 'Send message'}
            icon={isLoading ? MdStop : MdArrowUpward}
            width="2rem"
            height="2rem"
            isDisabled={!isLoading && !canSend}
            bg={isLoading || canSend ? 'tMagenta.base' : 'tGray.lightPurple'}
            iconColor="white"
            borderRadius="full"
            transition="all 0.15s ease"
            buttonColorHover={isLoading || canSend ? '#7B25E5' : '#B0AEBA'}
            flexShrink={0}
            alignSelf="flex-end"
            mb={0.5}
            onClick={handleButtonClick}
          />
        </form>
      </Box>
      <MFlex
        justifyContent="center"
        alignItems="center"
        flexWrap="wrap"
        columnGap={2}
        fontSize="xxs"
        color="fg.subtle"
        mt={1.5}
        opacity={0.7}
      >
        <Box>AI may produce inaccurate information. Verify before acting.</Box>
        <ChatSessionId />
      </MFlex>
    </Box>
  );
};
